/**
 * useFinanceiroParcelasPaginated - Hook de paginação server-side para parcelas individuais
 * 
 * Utiliza RPC get_parcelas_paginated com offset-based pagination
 * Replica o padrão de useFinanceiroCobrancasPaginated para consistência
 * 
 * @version 1.0.0
 * @author Sistema
 * RELEASE: 2026-03-17 v1.0.0 - Criação do hook para listagem de parcelas individuais
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('FinanceiroParcelasPaginated');

/**
 * Interface da parcela retornada pela RPC get_parcelas_paginated
 * Estrutura flat otimizada para tabelas (sem embeds aninhados)
 */
export interface PaginatedParcela {
  // Dados da parcela
  id: string;
  cobranca_id: string;
  numero_parcela: number;
  valor: number;
  data_vencimento: string;
  data_pagamento: string | null;
  valor_pago: number | null;
  status: string;
  metadata: Record<string, any> | null;
  parcela_created_at: string;
  // Dados da cobrança-pai (flat)
  descricao: string | null;
  moeda: string;
  tipo_cobranca: string;
  valor_total: number;
  numero_parcelas: number;
  provider_charge_id: string | null;
  provider_slug: string | null;
  provider_invoice_url: string | null;
  provider_payment_link: string | null;
  conta_corrente_id: string | null;
  meio_pagamento_id: string | null;
  customer_id: string | null;
  deal_id: string | null;
  // Dados do cliente (flat)
  customer_name: string | null;
  customer_document: string | null;
  // Meio pagamento e conta corrente
  tipo_pagamento: string | null;
  conta_corrente_nome: string | null;
  // Vínculo com módulo de Recorrência (Fase 6)
  assinatura_id: string | null;
  ciclo_numero: number | null;
  // KPIs globais (mesmo valor em todas as rows)
  kpi_total_parcelas: number;
  kpi_total_pagas: number;
  kpi_recebido: number;
  kpi_previsto: number;
  // Paginação (window function)
  total_count: number;
  /** Número da fatura no provider (ex: Asaas invoiceNumber) — coluna dedicada */
  numero_fatura: string | null;
  // Contingência de checkout (v1.6.0 RPC - 2026-06-21)
  /** Modo preferido herdado da conta corrente. */
  checkout_mode: 'transparent' | 'provider' | null;
  /** Slug ativo da sessão de checkout transparente (cache na cobrança-pai). */
  checkout_session_slug: string | null;
}

/**
 * Interface de filtros para listagem de parcelas
 */
export interface ParcelaPaginatedFilters {
  status?: string | null;
  meio_pagamento_id?: string | null;
  product_id?: string | null;
  category_id?: string | null;
  search?: string | null;
  data_vencimento_de?: string | null;
  data_vencimento_ate?: string | null;
  data_pagamento_de?: string | null;
  data_pagamento_ate?: string | null;
  customer_id?: string | null;
  deal_id?: string | null;
}

/**
 * Parâmetros de paginação
 */
interface PaginationParams {
  page: number;
  pageSize: number;
}

/**
 * Interface de KPIs extraídos da RPC
 */
export interface ParcelaKpis {
  totalParcelas: number;
  totalPagas: number;
  recebido: number;
  previsto: number;
}

/**
 * Resultado da query paginada
 */
export interface PaginatedParcelasResult {
  parcelas: PaginatedParcela[];
  totalCount: number;
  totalPages: number;
  kpis: ParcelaKpis;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook para buscar parcelas individuais com paginação server-side
 * Utiliza RPC get_parcelas_paginated para performance em grande escala
 * 
 * @param filters - Filtros para a busca (status, datas, etc.)
 * @param pagination - Parâmetros de paginação (page, pageSize)
 */
export function useFinanceiroParcelasPaginated(
  filters: ParcelaPaginatedFilters | undefined,
  pagination: PaginationParams
): PaginatedParcelasResult {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    // Query key inclui filtros e paginação para cache correto
    queryKey: ['financeiro-parcelas-paginated', currentTenant?.id, filters, pagination],
    queryFn: async () => {
      logger.debug('Buscando parcelas paginadas:', { filters, pagination });

      // Chama RPC com todos os parâmetros
      // NOTA: Casting necessário até types.ts ser regenerado
      const { data, error } = await (supabase.rpc as any)('get_parcelas_paginated', {
        p_page: pagination.page,
        p_page_size: pagination.pageSize,
        p_tenant_hint: currentTenant?.id || null,
        // Filtros
        p_status: filters?.status || null,
        p_meio_pagamento_id: filters?.meio_pagamento_id || null,
        p_product_id: filters?.product_id || null,
        p_search: filters?.search || null,
        // Filtros de data
        p_data_vencimento_de: filters?.data_vencimento_de || null,
        p_data_vencimento_ate: filters?.data_vencimento_ate || null,
        p_data_pagamento_de: filters?.data_pagamento_de || null,
        p_data_pagamento_ate: filters?.data_pagamento_ate || null,
        // Filtro por categoria do produto
        p_category_id: filters?.category_id || null,
        // Filtros para abas Financeiro (cliente/negócio)
        p_customer_id: filters?.customer_id || null,
        p_deal_id: filters?.deal_id || null,
      });

      if (error) {
        logger.error('Erro ao buscar parcelas paginadas:', error);
        throw error;
      }

      // Total count e KPIs vêm em cada row (pattern COUNT(*) OVER())
      const rawParcelas = (data || []) as PaginatedParcela[];
      const totalCount = rawParcelas[0]?.total_count ?? 0;
      const kpis: ParcelaKpis = {
        totalParcelas: Number(rawParcelas[0]?.kpi_total_parcelas ?? 0),
        totalPagas: Number(rawParcelas[0]?.kpi_total_pagas ?? 0),
        recebido: Number(rawParcelas[0]?.kpi_recebido ?? 0),
        previsto: Number(rawParcelas[0]?.kpi_previsto ?? 0),
      };

      logger.debug(`Recebidas ${rawParcelas.length} parcelas, total: ${totalCount}`);

      return {
        parcelas: rawParcelas,
        totalCount: Number(totalCount),
        totalPages: Math.ceil(Number(totalCount) / pagination.pageSize) || 1,
        kpis,
      };
    },
    enabled: !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    parcelas: query.data?.parcelas || [],
    totalCount: query.data?.totalCount || 0,
    totalPages: query.data?.totalPages || 1,
    kpis: query.data?.kpis || { totalParcelas: 0, totalPagas: 0, recebido: 0, previsto: 0 },
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      queryClient.invalidateQueries({
        queryKey: ['financeiro-parcelas-paginated', currentTenant?.id],
      });
    },
  };
}
